/**
 * Formatting utilities for subscription components
 * Uses i18n for locale-aware formatting
 */
import i18n from '../i18n';

/**
 * Get the full locale string from the current i18n language
 */
const getLocale = () => {
    const lang = i18n.language?.substring(0, 2) || 'pl';
    return lang === 'pl' ? 'pl-PL' : 'en-US';
};

/**
 * Format currency with current locale
 * @param {number} amount - Amount to format
 * @param {string} currency - Currency code (default: 'pln')
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, currency = 'pln') => {
    return new Intl.NumberFormat(getLocale(), {
        style: 'currency',
        currency: currency.toUpperCase()
    }).format(amount);
};

/**
 * Format billing interval
 * @param {string} interval - Interval (day, week, month, year)
 * @returns {string} Translated interval name
 */
export const formatInterval = (interval) => {
    return i18n.t(`common:formatters.interval.${interval}`, { defaultValue: interval });
};

/**
 * Format date with current locale
 * @param {string|Date} dateString - Date to format
 * @returns {string} Formatted date string
 */
export const formatDate = (dateString) => {
    if (!dateString) return i18n.t('common:formatters.notSpecified');
    return new Date(dateString).toLocaleDateString(getLocale(), {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    });
};

/**
 * Get subscription status text
 * @param {string} status - Status code
 * @returns {string} Translated status text
 */
export const getSubscriptionStatusText = (status) => {
    if (!status) return i18n.t('common:formatters.subscriptionStatus.unknown');
    return i18n.t(`common:formatters.subscriptionStatus.${status}`, {
        defaultValue: status
    });
};

/**
 * Get text for days remaining
 * @param {number} days - Number of days
 * @returns {string} Translated text for days
 */
export const getDaysText = (days) => {
    return i18n.t('common:formatters.day', { count: days });
};

/**
 * Calculate days between two dates
 * @param {Date|string} startDate - Start date
 * @param {Date|string} endDate - End date
 * @returns {number} Number of days (rounded up)
 */
export const calculateDaysRemaining = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = end - start;
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};
